import { Column, ColumnDef } from '@tanstack/react-table';
import { SourcingOpenings } from 'api-clients/monolith';
import _isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';

import { ChannelIcon } from '@/components/ui/channel-icon';
import { Checkbox } from '@/components/ui/checkbox';
import { CurrencyStats } from '@/components/ui/currency-stats';
import { Duration } from '@/components/ui/duration';
import { HireStats } from '@/components/ui/hire-stats';
import { OpeningStatus } from '@/components/ui/opening-status';
import { SortColumnButton } from '@/components/ui/sort-column-button';
import { Typography } from '@/components/ui/typography';

export type SelectOpeningRow = SourcingOpenings & {
  location_name?: string | null;
  location_address?: string | null;
};

function formatLocation(row: SelectOpeningRow): string {
  const parts = [row.location_name, row.location_address].filter(
    (part): part is string => Boolean(part),
  );
  return parts.join(', ') || '—';
}

export function useSelectOpeningsColumns(
  defaultMetricsPeriod?: number | null,
): ColumnDef<SelectOpeningRow>[] {
  const handleSort = (column: Column<SelectOpeningRow>) => {
    column.toggleSorting(column.getIsSorted() === 'asc');
  };

  return [
    {
      id: 'select',
      size: 32,
      meta: { narrowCell: true },
      header: ({ table }) => (
        <Checkbox
          className="pl-2"
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          className="pl-2"
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
        />
      ),
    },
    {
      accessorKey: 'title',
      size: 200,
      meta: { narrowCell: true, wrapCell: true },
      header: ({ column }) => (
        <SortColumnButton onSort={() => handleSort(column)}>
          <FormattedMessage
            defaultMessage="Opening name"
            description="Select-openings table: opening title column"
          />
        </SortColumnButton>
      ),
      // Product: titles stay plain text. This table selects openings to post.
      // Opening details stay on the openings list, not a link from this cell.
      cell: ({ row }) => (
        <div className="flex w-full items-center gap-1 overflow-hidden">
          <OpeningStatus active={row.original.active} />
          <Typography
            variant="body1"
            className="h-auto text-left whitespace-normal"
          >
            {row.original.title}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: 'location_name',
      size: 176,
      meta: { wrapCell: true },
      header: () => (
        <FormattedMessage
          defaultMessage="Location"
          description="Select-openings table: site name and address"
        />
      ),
      cell: ({ row }) => (
        <Typography variant="subtitle1" className="whitespace-normal">
          {formatLocation(row.original)}
        </Typography>
      ),
    },
    {
      accessorKey: 'hire_stats',
      size: 56,
      header: () => (
        <FormattedMessage
          defaultMessage="Hires"
          description="Select-openings table: actual versus expected hires"
        />
      ),
      cell: ({ row }) => (
        <HireStats
          stats={{
            actual: row.original.hire_stats.actual.total,
            expected: row.original.hire_stats.expected,
          }}
          showOnlyActual={!row.original.hire_stats.expected}
        />
      ),
    },
    {
      accessorKey: 'duration_stats',
      size: 88,
      header: () => (
        <FormattedMessage
          defaultMessage="Duration"
          description="Select-openings table: days elapsed versus hiring-goal length"
        />
      ),
      cell: ({ row }) => (
        <Duration
          stats={row.original.duration_stats}
          showMetricsPeriod={!row.original.hiring_goal}
          metricsPeriod={defaultMetricsPeriod}
        />
      ),
    },
    {
      accessorKey: 'budget_stats',
      size: 88,
      header: () => (
        <FormattedMessage
          defaultMessage="Spent / Budget"
          description="Select-openings table: campaign spend versus allotted budget"
        />
      ),
      cell: ({ row }) => <CurrencyStats stats={row.original.budget_stats} />,
    },
    {
      accessorKey: 'channels',
      size: 96,
      header: () => (
        <FormattedMessage
          defaultMessage="Channels"
          description="Select-openings table: job-board icons for this opening"
        />
      ),
      cell: ({ row }) => {
        const { channels } = row.original;

        if (_isEmpty(channels)) return <span>—</span>;

        const visibleChannels = channels?.slice(0, 5);
        const overflow =
          (channels?.length ?? 0) - (visibleChannels?.length ?? 0);

        return (
          <div className="flex w-max items-center">
            {visibleChannels?.map(channel => (
              <ChannelIcon
                key={channel.slug}
                variant="small"
                channelSlug={channel.slug}
                channelName={channel.name}
                logoSquareUrl={channel.logo_square}
                logoRectangleUrl={channel.logo_rectangle}
                className="-mr-1 last:mr-0"
                isRounded={true}
              />
            ))}
            {overflow > 0 && (
              <span className="text-fx-neutral-400 ml-2 text-xs">
                +{overflow}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'brand_name',
      size: 96,
      header: () => (
        <FormattedMessage
          defaultMessage="Brand"
          description="Select-openings table: employer brand"
        />
      ),
      cell: ({ row }) => (
        <Typography variant="subtitle1">{row.original.brand_name}</Typography>
      ),
    },
  ];
}
